import { useEffect } from "react";

/**
 * A row of links that scrolls sideways on a phone (the header's product row,
 * Home's product tabs):
 * - the current link is brought into view when the page changes, so a
 *   visitor on "Airport taxis" sees "Airport taxis" chosen rather than a row
 *   that starts at "Stays" with the choice off screen;
 * - `data-scroll-fade` says which ends still hide links ("start", "end" or
 *   "both"), and the stylesheet fades that edge (landing-theme.css), so the
 *   row reads as scrollable.
 * The page itself never scrolls: only the row's own scrollLeft moves.
 */
export function useScrollRow(ref, activeKey) {
  useEffect(() => {
    const row = ref.current;
    if (!row) return;
    const active = row.querySelector('[aria-current="page"], [data-category-active="true"]');
    if (!active || row.scrollWidth <= row.clientWidth + 1) return;
    const rowBox = row.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    if (box.left >= rowBox.left && box.right <= rowBox.right) return;
    const delta = box.left + box.width / 2 - (rowBox.left + rowBox.width / 2);
    if (typeof row.scrollBy === "function") row.scrollBy({ left: delta, behavior: "auto" });
    else row.scrollLeft += delta;
  }, [ref, activeKey]);

  useEffect(() => {
    const row = ref.current;
    if (!row) return undefined;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      if (max <= 1) { row.removeAttribute("data-scroll-fade"); return; }
      // scrollLeft runs negative in a right-to-left row: the distance from the start is its absolute value.
      const fromStart = Math.abs(row.scrollLeft);
      const atStart = fromStart <= 1;
      const atEnd = fromStart >= max - 1;
      row.setAttribute("data-scroll-fade", atStart ? "end" : atEnd ? "start" : "both");
    };
    update();
    row.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    observer?.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [ref]);
}
